'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    discardSalesFlowDraft,
    getSalesFlow,
    saveSalesFlowDraft,
    getSalesFlowStats,
    publishSalesFlow,
    setSalesFlowActive,
    updateSalesFlow,
    type SalesFlow,
    type SalesFlowEdge,
    type SalesFlowNode,
    type SalesFlowSettingsData,
    type SalesFlowTrigger,
} from '@/lib/api/sales/flows-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import {
    SALES_FLOW_NODE_HANDLES,
    validateSalesFlowGraph,
    type SalesFlowGraphInput,
    type SalesFlowHandle,
    type SalesFlowNodeType,
} from '@mediardex/contracts'
import {
    Background,
    BackgroundVariant,
    Connection,
    Controls,
    Edge,
    EdgeChange,
    MarkerType,
    MiniMap,
    Node,
    NodeChange,
    ReactFlow,
    ReactFlowProvider,
    useReactFlow,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    MdAccountTree,
    MdAdd,
    MdArrowBack,
    MdAutoFixHigh,
    MdErrorOutline,
    MdHistory,
    MdPlayArrow,
    MdSave,
    MdSettings,
    MdBolt,
} from 'react-icons/md'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import FlowCanvasNode, { type FlowCanvasNodeData } from './components/flow-canvas-node'
import NodeConfigPanel from './components/node-config-panel'
import SettingsPanel from './components/settings-panel'
import SimulateModal from './components/simulate-modal'
import TriggersPanel from './components/triggers-panel'
import { FLOW_HANDLE_LABELS, FLOW_NODE_GROUPS, FLOW_NODE_META } from './flow-meta'
import { FLOW_TEMPLATES } from './flow-templates'
import { useFlowRefData } from './use-flow-ref-data'

type SidebarTab = 'triggers' | 'settings' | 'issues'

type Settings = Required<Pick<SalesFlowSettingsData, 'name'>> & SalesFlowSettingsData

const NODE_TYPES = { flow: FlowCanvasNode }
const COLUMN_WIDTH = 400
const ROW_HEIGHT = 200

const settingsOf = (flow: SalesFlow): Settings => ({
    name: flow.name,
    description: flow.description ?? '',
    launchable: flow.launchable,
    departmentId: flow.departmentId ?? null,
    activeWindow: flow.activeWindow ?? null,
    exitRules: flow.exitRules ?? null,
    reentryCooldownHours: flow.reentryCooldownHours ?? null,
    priority: flow.priority,
})

const graphOf = (triggers: SalesFlowTrigger[], nodes: SalesFlowNode[], edges: SalesFlowEdge[]): SalesFlowGraphInput => ({
    triggers: triggers.map(t => ({
        type: t.type,
        event: t.event ?? null,
        pipelineId: t.pipelineId ?? null,
        stageId: t.stageId ?? null,
        companyId: t.companyId ?? null,
        delayMinutes: t.delayMinutes ?? 0,
        config: t.config ?? null,
        conditions: t.conditions?.length ? t.conditions : null,
        conditionLogic: t.conditionLogic ?? 'AND',
    })),
    nodes: nodes.map(n => ({ key: n.key, type: n.type, config: n.config ?? {}, positionX: Math.round(n.positionX), positionY: Math.round(n.positionY) })),
    edges: edges.map(e => ({ fromKey: e.fromKey, handle: e.handle, toKey: e.toKey })),
})

/**
 * Passo criado fora do editor (API, fluxo de sistema, migração) nasce em 0,0: sem isto os
 * cartões abrem empilhados. Organiza só quando dois passos dividem a mesma posição.
 */
function layoutIfStacked(nodes: SalesFlowNode[], edges: SalesFlowEdge[]): SalesFlowNode[] {
    const positions = new Set(nodes.map(n => `${Math.round(n.positionX ?? 0)}:${Math.round(n.positionY ?? 0)}`))
    return positions.size < nodes.length ? autoLayout(nodes, edges) : nodes
}

/** Colunas pela distância do início; linhas pela ordem de chegada. Para fluxos migrados ou bagunçados. */
function autoLayout(nodes: SalesFlowNode[], edges: SalesFlowEdge[]): SalesFlowNode[] {
    const start = nodes.find(n => n.type === 'START')
    if (!start) return nodes
    const depth = new Map<string, number>([[start.key, 0]])
    const queue = [start.key]
    while (queue.length) {
        const key = queue.shift()!
        const targets = edges.filter(e => e.fromKey === key).map(e => e.toKey)
        const node = nodes.find(n => n.key === key)
        if (node?.type === 'GO_TO' && node.config?.targetKey) targets.push(node.config.targetKey)
        for (const target of targets) {
            if (!depth.has(target)) {
                depth.set(target, depth.get(key)! + 1)
                queue.push(target)
            }
        }
    }
    const maxDepth = Math.max(0, ...depth.values())
    const rows = new Map<number, number>()
    return nodes.map(n => {
        const column = depth.get(n.key) ?? maxDepth + 1
        const row = rows.get(column) ?? 0
        rows.set(column, row + 1)
        return { ...n, positionX: column * COLUMN_WIDTH, positionY: row * ROW_HEIGHT }
    })
}

function FlowEditor() {
    const { id } = useParams<{ id: string }>()
    const flowId = Number(id)
    const navigate = useNavigate()
    const location = useLocation()
    // Lido uma vez: o modelo escolhido na criação só vale para a primeira carga.
    const pendingTemplate = useRef<string | undefined>((location.state as { template?: string } | null)?.template)
    const { canUpdate } = useResourcePermissions('sales', 'automations')
    const refData = useFlowRefData()
    const reactFlow = useReactFlow()

    const [flow, setFlow] = useState<SalesFlow | null>(null)
    const [settings, setSettings] = useState<Settings>({ name: '' })
    const [triggers, setTriggers] = useState<SalesFlowTrigger[]>([])
    const [nodes, setNodes] = useState<SalesFlowNode[]>([])
    const [edges, setEdges] = useState<SalesFlowEdge[]>([])
    const [savedSnapshot, setSavedSnapshot] = useState('')
    const [selectedKey, setSelectedKey] = useState<string | null>(null)
    const [tab, setTab] = useState<SidebarTab>('triggers')
    const [reached, setReached] = useState<Record<string, number>>({})
    // Medidas que o React Flow calcula: sem devolvê-las nos nós controlados, o minimapa fica vazio.
    const [measured, setMeasured] = useState<Record<string, { width: number; height: number }>>({})
    const [saving, setSaving] = useState(false)
    const [toggling, setToggling] = useState(false)
    const [showSimulate, setShowSimulate] = useState(false)
    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
    const [discarding, setDiscarding] = useState(false)
    // Rascunho: salvo sozinho a cada alteração. `persistedSnapshot` é o que o servidor já tem
    // (rascunho ou, sem rascunho, a versão publicada).
    const persistedSnapshot = useRef('')
    const [draftSavedAt, setDraftSavedAt] = useState<Date | null>(null)
    const [draftStatus, setDraftStatus] = useState<'idle' | 'saving' | 'error'>('idle')
    const [showPalette, setShowPalette] = useState(false)
    const paletteRef = useRef<HTMLDivElement>(null)

    const snapshot = useMemo(() => JSON.stringify({ settings, graph: graphOf(triggers, nodes, edges) }), [settings, triggers, nodes, edges])
    const dirty = !!flow && snapshot !== savedSnapshot

    const load = useCallback(async () => {
        const res = await getSalesFlow(flowId)
        if (!res.success) {
            toast.error(res.error || 'Fluxo não encontrado')
            navigate('/sales/automations')
            return
        }
        const loaded = res.result
        const loadedSettings = settingsOf(loaded)
        const loadedTriggers = loaded.triggers ?? []
        const loadedEdges = loaded.edges ?? []
        const loadedNodes = layoutIfStacked(loaded.nodes ?? [], loadedEdges)
        const publishedSnapshot = JSON.stringify({ settings: loadedSettings, graph: graphOf(loadedTriggers, loadedNodes, loadedEdges) })
        setFlow(loaded)
        setSavedSnapshot(publishedSnapshot)
        setDraftStatus('idle')

        // Há rascunho: o editor abre nele, não na versão publicada.
        if (loaded.draft?.graph) {
            const draftSettings = { ...loadedSettings, ...(loaded.draft.settings ?? {}) } as Settings
            const draftTriggers = (loaded.draft.graph.triggers ?? []) as SalesFlowTrigger[]
            const draftEdges = (loaded.draft.graph.edges ?? []) as SalesFlowEdge[]
            const draftNodes = layoutIfStacked((loaded.draft.graph.nodes ?? []) as SalesFlowNode[], draftEdges)
            setSettings(draftSettings)
            setTriggers(draftTriggers)
            setNodes(draftNodes)
            setEdges(draftEdges)
            persistedSnapshot.current = JSON.stringify({ settings: draftSettings, graph: graphOf(draftTriggers, draftNodes, draftEdges) })
            setDraftSavedAt(loaded.draftSavedAt ? new Date(loaded.draftSavedAt) : null)
            pendingTemplate.current = undefined
            return
        }
        persistedSnapshot.current = publishedSnapshot
        setDraftSavedAt(null)
        setSettings(loadedSettings)

        // Recém-criado a partir de um modelo: o desenho entra como rascunho (salvo logo em seguida).
        const template = FLOW_TEMPLATES.find(t => t.id === pendingTemplate.current)
        pendingTemplate.current = undefined
        if (template && loadedNodes.length <= 1 && template.nodes.length > 1) {
            setTriggers(structuredClone(template.triggers))
            setNodes(structuredClone(template.nodes))
            setEdges(structuredClone(template.edges))
            navigate(location.pathname, { replace: true, state: null })
            return
        }
        setTriggers(loadedTriggers)
        setNodes(loadedNodes)
        setEdges(loadedEdges)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flowId, navigate])

    useEffect(() => {
        load()
        getSalesFlowStats(flowId).then(res => {
            if (!res.success) return
            const counts: Record<string, number> = {}
            for (const stat of res.result) {
                // "Aguardando" é registrado de novo ao retomar: contar só os que saíram do passo.
                if (stat.status === 'WAITING') continue
                counts[stat.nodeKey] = (counts[stat.nodeKey] ?? 0) + stat._count._all
            }
            setReached(counts)
        })
    }, [flowId, load])

    useEffect(() => {
        if (!showPalette) return
        const close = (event: MouseEvent) => {
            if (paletteRef.current && !paletteRef.current.contains(event.target as globalThis.Node)) setShowPalette(false)
        }
        document.addEventListener('mousedown', close)
        return () => document.removeEventListener('mousedown', close)
    }, [showPalette])

    const persistDraft = useCallback(async (): Promise<boolean> => {
        if (!flow || !canUpdate) return true
        const current = snapshot
        if (current === persistedSnapshot.current) return true
        setDraftStatus('saving')
        // Voltou exatamente à versão publicada: o rascunho não serve mais.
        const res =
            current === savedSnapshot
                ? await discardSalesFlowDraft(flow.id)
                : await saveSalesFlowDraft(flow.id, { settings, graph: graphOf(triggers, nodes, edges) })
        if (!res.success) {
            setDraftStatus('error')
            return false
        }
        persistedSnapshot.current = current
        setDraftSavedAt(current === savedSnapshot ? null : new Date())
        setDraftStatus('idle')
        return true
    }, [flow, canUpdate, snapshot, savedSnapshot, settings, triggers, nodes, edges])

    useEffect(() => {
        if (!flow || !canUpdate || snapshot === persistedSnapshot.current) return
        const timer = setTimeout(() => persistDraft(), 1500)
        return () => clearTimeout(timer)
    }, [flow, canUpdate, snapshot, persistDraft])

    // Enquadra quando todos os cartões já foram medidos: antes disso o React Flow adia o
    // `fitView` e ele sobrescreve qualquer ajuste. Fluxo comprido não vira miniatura
    // ilegível: começa pelo Início, em zoom de leitura, e o usuário navega.
    const fitted = useRef(false)
    const measuredCount = Object.keys(measured).length
    useEffect(() => {
        if (fitted.current || nodes.length === 0 || measuredCount < nodes.length) return
        fitted.current = true
        const minReadableZoom = 0.6
        reactFlow.fitView({ padding: 0.15, maxZoom: 1 })
        const start = nodes.find(n => n.type === 'START')
        if (start && reactFlow.getZoom() < minReadableZoom) {
            const canvas = document.querySelector('.react-flow')?.getBoundingClientRect()
            const centerY = (canvas?.height ?? 600) / 2
            reactFlow.setViewport({ x: 60 - start.positionX * minReadableZoom, y: centerY - (start.positionY + 50) * minReadableZoom, zoom: minReadableZoom })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [measuredCount, nodes.length])

    const issues = useMemo(() => validateSalesFlowGraph(graphOf(triggers, nodes, edges)), [triggers, nodes, edges])
    const errors = issues.filter(i => i.severity === 'error')
    const selectedNode = nodes.find(n => n.key === selectedKey) ?? null

    // ================= Canvas =================

    const rfNodes: Node[] = useMemo(
        () =>
            nodes.map(n => ({
                id: n.key,
                type: 'flow',
                position: { x: n.positionX, y: n.positionY },
                measured: measured[n.key],
                selected: n.key === selectedKey,
                deletable: n.type !== 'START',
                data: {
                    nodeKey: n.key,
                    type: n.type,
                    config: n.config ?? {},
                    refData,
                    issues: issues.filter(i => i.nodeKey === n.key),
                    reached: reached[n.key],
                } satisfies FlowCanvasNodeData,
            })),
        [nodes, selectedKey, refData, issues, reached, measured],
    )

    const rfEdges: Edge[] = useMemo(
        () =>
            edges.map(e => ({
                id: `${e.fromKey}:${e.handle}`,
                source: e.fromKey,
                sourceHandle: e.handle,
                target: e.toKey,
                type: 'smoothstep',
                label: e.handle === 'next' ? undefined : FLOW_HANDLE_LABELS[e.handle],
                labelStyle: { fontSize: 11, fontWeight: 500 },
                labelBgPadding: [6, 3] as [number, number],
                labelBgBorderRadius: 6,
                // Traço grosso e escuro: o cinza de 1px padrão some com o zoom afastado.
                // `non-scaling-stroke`: a espessura não encolhe com o zoom afastado.
                style:
                    e.handle === 'failed'
                        ? { stroke: '#dc2626', strokeWidth: 2, strokeDasharray: '6 4', vectorEffect: 'non-scaling-stroke' }
                        : { stroke: '#64748b', strokeWidth: 2, vectorEffect: 'non-scaling-stroke' },
                markerEnd: { type: MarkerType.ArrowClosed, width: 18, height: 18, color: e.handle === 'failed' ? '#dc2626' : '#64748b' },
            })),
        [edges],
    )

    const onNodesChange = useCallback((changes: NodeChange[]) => {
        for (const change of changes) {
            if (change.type === 'position' && change.position) {
                const { x, y } = change.position
                setNodes(prev => prev.map(n => (n.key === change.id ? { ...n, positionX: x, positionY: y } : n)))
            }
            if (change.type === 'dimensions' && change.dimensions) {
                const { width, height } = change.dimensions
                setMeasured(prev => (prev[change.id]?.width === width && prev[change.id]?.height === height ? prev : { ...prev, [change.id]: { width, height } }))
            }
            if (change.type === 'select' && change.selected) setSelectedKey(change.id)
            if (change.type === 'remove') removeNode(change.id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onEdgesChange = useCallback((changes: EdgeChange[]) => {
        const removed = changes.filter(c => c.type === 'remove').map(c => c.id)
        if (removed.length) setEdges(prev => prev.filter(e => !removed.includes(`${e.fromKey}:${e.handle}`)))
    }, [])

    const onConnect = useCallback((connection: Connection) => {
        if (!connection.source || !connection.target || connection.source === connection.target) return
        const handle = (connection.sourceHandle ?? 'next') as SalesFlowHandle
        // Cada saída leva a um único passo: ligar de novo substitui a ligação anterior.
        setEdges(prev => [
            ...prev.filter(e => !(e.fromKey === connection.source && e.handle === handle)),
            { fromKey: connection.source!, handle, toKey: connection.target! },
        ])
    }, [])

    // ================= Passos =================

    const nextKey = (type: SalesFlowNodeType) => {
        const base = type.toLowerCase().replace(/_/g, '-')
        let index = 1
        const keys = new Set(nodes.map(n => n.key))
        while (keys.has(`${base}-${index}`)) index++
        return `${base}-${index}`
    }

    const addNode = (type: SalesFlowNodeType) => {
        const key = nextKey(type)
        const anchor = selectedNode ?? [...nodes].sort((a, b) => b.positionX - a.positionX)[0]
        const position = anchor
            ? { x: anchor.positionX + COLUMN_WIDTH, y: anchor.positionY }
            : reactFlow.screenToFlowPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 })
        const created: SalesFlowNode = {
            key,
            type,
            config: type === 'PAUSE' ? { timeoutMinutes: 1440, waitForReply: true } : type === 'TAG' ? { mode: 'add' } : type === 'SET_FIELD' ? { entity: 'lead' } : {},
            positionX: position.x,
            positionY: position.y,
        }
        setNodes(prev => [...prev, created])

        // Liga automaticamente a partir do passo selecionado, na primeira saída livre.
        if (selectedNode) {
            const freeHandle = SALES_FLOW_NODE_HANDLES[selectedNode.type].find(
                h => h !== 'failed' && !edges.some(e => e.fromKey === selectedNode.key && e.handle === h),
            )
            if (freeHandle) setEdges(prev => [...prev, { fromKey: selectedNode.key, handle: freeHandle, toKey: key }])
        }
        setSelectedKey(key)
        setShowPalette(false)
        // Traz o passo novo para a vista, descontando o painel da direita que vai abrir.
        setTimeout(() => {
            const zoom = reactFlow.getZoom()
            reactFlow.setCenter(position.x + 130 + 190 / zoom, position.y + 60, { zoom, duration: 300 })
        }, 50)
    }

    const removeNode = (key: string) => {
        setNodes(prev => prev.filter(n => n.key !== key || n.type === 'START'))
        setEdges(prev => prev.filter(e => e.fromKey !== key && e.toKey !== key))
        setSelectedKey(prev => (prev === key ? null : prev))
    }

    const duplicateNode = (key: string) => {
        const source = nodes.find(n => n.key === key)
        if (!source || source.type === 'START') return
        const copy: SalesFlowNode = { ...source, key: nextKey(source.type), config: structuredClone(source.config ?? {}), positionX: source.positionX + 40, positionY: source.positionY + 160 }
        setNodes(prev => [...prev, copy])
        setSelectedKey(copy.key)
    }

    const updateNodeConfig = (key: string, config: Record<string, any>) =>
        setNodes(prev => prev.map(n => (n.key === key ? { ...n, config } : n)))

    const organize = () => {
        setNodes(prev => autoLayout(prev, edges))
        setTimeout(() => reactFlow.fitView({ padding: 0.2, duration: 300 }), 50)
    }

    // ================= Salvar e ativar =================

    const save = async (): Promise<boolean> => {
        if (!flow) return false
        if (!settings.name.trim()) {
            toast.error('Dê um nome ao fluxo')
            setTab('settings')
            return false
        }
        if (errors.length) {
            toast.error('Corrija os problemas do fluxo antes de publicar')
            setTab('issues')
            return false
        }
        setSaving(true)
        try {
            const settingsRes = await updateSalesFlow(flow.id, { ...settings, name: settings.name.trim() })
            if (!settingsRes.success) {
                toast.error(settingsRes.error || 'Erro ao salvar as configurações')
                return false
            }
            const publishRes = await publishSalesFlow(flow.id, graphOf(triggers, nodes, edges))
            if (!publishRes.success) {
                toast.error(publishRes.error || 'Erro ao publicar o fluxo')
                return false
            }
            toast.success('Fluxo publicado')
            await load()
            return true
        } finally {
            setSaving(false)
        }
    }

    const toggleActive = async () => {
        if (!flow) return
        if (!flow.active && dirty && !(await save())) return
        setToggling(true)
        const res = await setSalesFlowActive(flow.id, !flow.active)
        setToggling(false)
        if (!res.success) {
            toast.error(res.error || 'Não foi possível alterar o status')
            setTab('issues')
            return
        }
        toast.success(res.result.active ? 'Fluxo ativado' : 'Fluxo desativado')
        await load()
    }

    // Sai salvando o rascunho pendente: nada se perde por ter voltado antes do salvamento automático.
    const goBack = async () => {
        await persistDraft()
        navigate('/sales/automations')
    }

    const discardDraft = async () => {
        if (!flow) return
        setDiscarding(true)
        const res = await discardSalesFlowDraft(flow.id)
        setDiscarding(false)
        setShowDiscardConfirm(false)
        if (!res.success) {
            toast.error(res.error || 'Não foi possível descartar o rascunho')
            return
        }
        toast.success('Rascunho descartado')
        await load()
    }

    const draftLabel =
        draftStatus === 'saving'
            ? 'Salvando rascunho…'
            : draftStatus === 'error'
              ? 'Não foi possível salvar o rascunho'
              : draftSavedAt
                ? `Rascunho salvo às ${draftSavedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
                : null

    if (!flow) {
        return (
            <div className="flex h-full items-center justify-center">
                <span className="loading loading-spinner loading-lg" />
            </div>
        )
    }

    const readOnly = !canUpdate

    return (
        <div className="flex h-full flex-col bg-base-200">
            {/* Cabeçalho */}
            <header className="flex flex-wrap items-center gap-3 border-b border-base-300 bg-base-100 px-4 py-3">
                <button type="button" className="btn btn-ghost btn-sm btn-square" onClick={goBack} aria-label="Voltar">
                    <MdArrowBack className="text-lg" />
                </button>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <h1 className="truncate text-lg font-semibold">{settings.name || 'Sem nome'}</h1>
                        <span className={`badge badge-sm ${flow.active ? 'badge-success' : 'badge-ghost'}`}>{flow.active ? 'Ativo' : 'Inativo'}</span>
                        {dirty && <span className="badge badge-warning badge-sm">Rascunho não publicado</span>}
                        {flow.legacyRef && <span className="badge badge-outline badge-sm">Migrado</span>}
                    </div>
                    <p className="text-xs text-base-content/50">
                        Versão publicada {flow.version}
                        {draftLabel && <span className={draftStatus === 'error' ? 'text-error' : ''}> · {draftLabel}</span>}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button type="button" className="btn btn-ghost btn-sm gap-1" onClick={() => navigate(`/sales/automations?tab=runs&flowId=${flow.id}`)}>
                        <MdHistory /> Execuções
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm gap-1" onClick={() => setShowSimulate(true)}>
                        <MdPlayArrow /> Simular
                    </button>
                    {!readOnly && (
                        <>
                            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-base-300 px-3 py-1.5">
                                <span className="text-sm">Ativo</span>
                                <input type="checkbox" className="toggle toggle-success toggle-sm" checked={flow.active} disabled={toggling} onChange={toggleActive} />
                            </label>
                            {dirty && (
                                <button type="button" className="btn btn-ghost btn-sm" disabled={discarding} onClick={() => setShowDiscardConfirm(true)}>
                                    Descartar rascunho
                                </button>
                            )}
                            <button type="button" className="btn btn-primary btn-sm gap-1" disabled={saving || !dirty} onClick={save}>
                                {saving ? <span className="loading loading-spinner loading-xs" /> : <MdSave />}
                                Publicar
                            </button>
                        </>
                    )}
                </div>
            </header>

            <div className="flex min-h-0 flex-1">
                {/* Painel esquerdo */}
                <aside className="flex w-[340px] shrink-0 flex-col border-r border-base-300 bg-base-100">
                    <div role="tablist" className="tabs tabs-bordered px-2 pt-2">
                        <button role="tab" type="button" className={`tab gap-1 ${tab === 'triggers' ? 'tab-active' : ''}`} onClick={() => setTab('triggers')}>
                            <MdBolt /> Gatilhos
                        </button>
                        <button role="tab" type="button" className={`tab gap-1 ${tab === 'settings' ? 'tab-active' : ''}`} onClick={() => setTab('settings')}>
                            <MdSettings /> Fluxo
                        </button>
                        <button role="tab" type="button" className={`tab gap-1 ${tab === 'issues' ? 'tab-active' : ''}`} onClick={() => setTab('issues')}>
                            <MdErrorOutline /> Problemas
                            {issues.length > 0 && <span className={`badge badge-xs ${errors.length ? 'badge-error' : 'badge-warning'}`}>{issues.length}</span>}
                        </button>
                    </div>
                    <fieldset disabled={readOnly} className="min-h-0 flex-1 overflow-y-auto p-4">
                        {tab === 'triggers' && <TriggersPanel triggers={triggers} refData={refData} onChange={setTriggers} />}
                        {tab === 'settings' && <SettingsPanel settings={settings} onChange={patch => setSettings(prev => ({ ...prev, ...patch }))} />}
                        {tab === 'issues' &&
                            (issues.length === 0 ? (
                                <p className="text-sm text-base-content/60">Nenhum problema encontrado.</p>
                            ) : (
                                <ul className="space-y-2">
                                    {issues.map((issue, index) => (
                                        <li key={index}>
                                            <button
                                                type="button"
                                                className={`alert w-full py-2 text-left text-sm ${issue.severity === 'error' ? 'alert-error' : 'alert-warning'}`}
                                                onClick={() => {
                                                    if (!issue.nodeKey) return setTab('triggers')
                                                    setSelectedKey(issue.nodeKey)
                                                    const target = nodes.find(n => n.key === issue.nodeKey)
                                                    if (target) reactFlow.setCenter(target.positionX + 130, target.positionY + 60, { zoom: 1, duration: 300 })
                                                }}
                                            >
                                                {issue.nodeKey && <span className="font-mono text-xs opacity-70">{issue.nodeKey}</span>}
                                                <span>{issue.message}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            ))}
                    </fieldset>
                </aside>

                {/* Canvas */}
                <main className="relative min-w-0 flex-1">
                    {!readOnly && (
                        <div className="absolute left-3 top-3 z-20 flex gap-2" ref={paletteRef}>
                            <div className="relative">
                                <button type="button" className="btn btn-primary btn-sm gap-1 shadow" onClick={() => setShowPalette(v => !v)}>
                                    <MdAdd /> Adicionar passo
                                </button>
                                {showPalette && (
                                    <div className="absolute left-0 top-10 max-h-[70vh] w-80 overflow-y-auto rounded-xl border border-base-300 bg-base-100 p-2 shadow-xl">
                                        {selectedNode && (
                                            <p className="px-2 pb-2 pt-1 text-xs text-base-content/50">
                                                Será ligado depois de "{FLOW_NODE_META[selectedNode.type].label}".
                                            </p>
                                        )}
                                        {FLOW_NODE_GROUPS.map(group => (
                                            <div key={group.id} className="mb-1">
                                                <p className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-base-content/40">{group.label}</p>
                                                {(Object.keys(FLOW_NODE_META) as SalesFlowNodeType[])
                                                    .filter(type => FLOW_NODE_META[type].group === group.id && !FLOW_NODE_META[type].hidden)
                                                    .map(type => (
                                                        <button
                                                            key={type}
                                                            type="button"
                                                            className="flex w-full items-start gap-3 rounded-lg px-2 py-2 text-left hover:bg-base-200"
                                                            onClick={() => addNode(type)}
                                                        >
                                                            <span className="mt-0.5 text-lg text-base-content/60">{FLOW_NODE_META[type].icon}</span>
                                                            <span>
                                                                <span className="block text-sm font-medium">{FLOW_NODE_META[type].label}</span>
                                                                <span className="block text-xs text-base-content/50">{FLOW_NODE_META[type].description}</span>
                                                            </span>
                                                        </button>
                                                    ))}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <button type="button" className="btn btn-sm gap-1 bg-base-100 shadow" onClick={organize}>
                                <MdAutoFixHigh /> Organizar
                            </button>
                        </div>
                    )}

                    <ReactFlow
                        nodes={rfNodes}
                        edges={rfEdges}
                        nodeTypes={NODE_TYPES}
                        onNodesChange={readOnly ? undefined : onNodesChange}
                        onEdgesChange={readOnly ? undefined : onEdgesChange}
                        onConnect={readOnly ? undefined : onConnect}
                        onPaneClick={() => setSelectedKey(null)}
                        nodesDraggable={!readOnly}
                        nodesConnectable={!readOnly}
                        deleteKeyCode={readOnly ? null : ['Delete', 'Backspace']}
                        fitView
                        fitViewOptions={{ padding: 0.2 }}
                        minZoom={0.2}
                        proOptions={{ hideAttribution: true }}
                    >
                        <Background variant={BackgroundVariant.Dots} gap={18} size={1} />
                        <Controls position="bottom-left" />
                        <MiniMap position="bottom-right" pannable zoomable nodeColor="#c7c9d1" nodeBorderRadius={6} className="!bg-base-100" />
                    </ReactFlow>

                    {nodes.length <= 1 && (
                        <div className="pointer-events-none absolute inset-x-0 top-16 z-[5] flex justify-center">
                            <div className="flex items-center gap-3 rounded-xl border border-dashed border-base-300 bg-base-100/90 px-5 py-3">
                                <MdAccountTree className="text-2xl text-base-content/30" />
                                <p className="text-sm text-base-content/60">Selecione o Início e use "Adicionar passo" para montar o fluxo.</p>
                            </div>
                        </div>
                    )}
                </main>

                {/* Painel do passo */}
                {selectedNode && (
                    <div className="w-[380px] shrink-0 border-l border-base-300">
                        <fieldset disabled={readOnly} className="h-full">
                            <NodeConfigPanel
                                key={selectedNode.key}
                                node={selectedNode}
                                nodes={nodes}
                                refData={refData}
                                issues={issues.filter(i => i.nodeKey === selectedNode.key)}
                                flowId={flow.id}
                                onChange={config => updateNodeConfig(selectedNode.key, config)}
                                onDelete={() => removeNode(selectedNode.key)}
                                onDuplicate={() => duplicateNode(selectedNode.key)}
                                onClose={() => setSelectedKey(null)}
                            />
                        </fieldset>
                    </div>
                )}
            </div>

            {showSimulate && <SimulateModal flowId={flow.id} nodes={nodes} dirty={dirty} onClose={() => setShowSimulate(false)} />}

            <ConfirmModal
                isOpen={showDiscardConfirm}
                onClose={() => setShowDiscardConfirm(false)}
                onConfirm={discardDraft}
                loading={discarding}
                title="Descartar rascunho"
                message="As alterações não publicadas serão apagadas e o editor volta para a versão publicada."
                confirmButtonText="Descartar"
                cancelButtonText="Continuar editando"
            />
        </div>
    )
}

export default function FlowEditorPage() {
    return (
        <ReactFlowProvider>
            <FlowEditor />
        </ReactFlowProvider>
    )
}
